import React from 'react';
import { colors, typography, buttons } from '../styles/theme';
import { buttons as buttonStyles } from '../styles/components';
import errorTracker from '../services/errorTracking';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error: error,
      errorInfo: errorInfo
    });
    
    // Send to error tracking service
    errorTracker.captureComponentError(error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.href = '/app';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div style={styles.container}>
          <div style={styles.card}>
            <div style={styles.iconContainer}>
              <span style={styles.icon}>⚠️</span>
            </div>
            <h2 style={styles.title}>Something went wrong</h2>
            <p style={styles.message}>
              We're sorry, but something unexpected happened. The error has been logged and we'll look into it.
            </p>
            
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details style={styles.details}>
                <summary style={styles.summary}>Error details (dev only)</summary>
                <pre style={styles.errorText}>
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}
            
            <div style={styles.actions}>
              <button onClick={this.handleReset} style={styles.primaryButton}>
                Go to Dashboard
              </button>
              <button onClick={() => window.location.reload()} style={styles.secondaryButton}>
                Reload Page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const styles = {
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    backgroundColor: colors.bgSoft,
    padding: '20px',
  },
  card: {
    backgroundColor: colors.white,
    borderRadius: '12px',
    padding: '40px',
    maxWidth: '600px',
    width: '100%',
    boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
    textAlign: 'center',
  },
  iconContainer: {
    marginBottom: '20px',
  },
  icon: {
    fontSize: '64px',
  },
  title: {
    ...typography.h2,
    color: colors.textDark,
    marginBottom: '12px',
  },
  message: {
    ...typography.body,
    color: colors.textMuted,
    marginBottom: '24px',
    lineHeight: '1.6',
  },
  details: {
    marginTop: '20px',
    marginBottom: '20px',
    textAlign: 'left',
    backgroundColor: colors.bgSoft,
    padding: '16px',
    borderRadius: '8px',
    border: `1px solid ${colors.border}`,
  },
  summary: {
    cursor: 'pointer',
    fontWeight: 600,
    color: colors.text,
    marginBottom: '12px',
  },
  errorText: {
    fontSize: '12px',
    color: colors.danger,
    overflow: 'auto',
    maxHeight: '200px',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  actions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  primaryButton: {
    ...buttonStyles.primary,
    padding: '12px 24px',
    fontSize: '16px',
    border: 'none',
    cursor: 'pointer',
  },
  secondaryButton: {
    ...buttonStyles.secondary,
    padding: '12px 24px',
    fontSize: '16px',
    cursor: 'pointer',
  },
};

export default ErrorBoundary;
